import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CampaignService from "../../service/CampaignService";
import { useForm } from "react-hook-form";
import { parse, isValid } from "date-fns";

import { resizeImage } from "../../utils/imageUtils";
import UploadFileService from "../../service/UploadFileService";

function AlertMessage({ type, message }) {
  // 메시지 타입에 따라 다른 스타일을 적용합니다.
  const backgroundColor = type === "success" ? "bg-green-100" : "bg-red-100";
  const textColor = type === "success" ? "text-green-800" : "text-red-800";

  return (
    <div className={`${backgroundColor} p-4 rounded-md shadow-md`}>
      <p className={`${textColor} font-semibold`}>{message}</p>
    </div>
  );
}

function ArrowLeftIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </svg>
  );
}

function BackIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function isValidDate(value) {
  if (value.length !== 8) {
    return false; // 정확히 8자리가 아니면 유효하지 않음
  }

  const year = parseInt(value.substring(0, 4), 10);
  const month = parseInt(value.substring(4, 6), 10) - 1; // 월은 0부터 시작
  const day = parseInt(value.substring(6, 8), 10);

  const date = new Date(year, month, day);
  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day; // 입력된 연, 월, 일이 `Date` 객체와 일치하는지 확인
}

export default function CustomerInfoEdit({ toggleDrawerProp, orderNo, salesNo, orderKey, customerNo }) {
  const [selectedGender, setSelectedGender] = useState("");
  const [fileName, setFileName] = useState("");
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const navigate = useNavigate();
  const [travelerInfo, setTravelerInfo] = useState(null);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { isSubmitting, isSubmitted, errors },
  } = useForm();
  const [errorMessage, setErrorMessage] = useState("");
  const [title, setTitle] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [selectedImage, setSelectedImage] = useState("");
  const [usePassport, setUsePassport] = useState(false); //여권정보필요여부

  // 폼 값들을 실시간으로 감시
  const watchedValues = watch();

  // 모든 필수 필드가 입력되었는지 확인하는 함수
  const isFormValid = () => {
    const { 한글이름, 성별, 영문성, 영문이름, 생년월일 } = watchedValues;
    return 한글이름 && 성별 && 영문성 && 영문이름 && 생년월일;
  };

  const handleSelectGender = (gender) => {
    setSelectedGender(gender);
    setValue("성별", gender); // react-hook-form에 성별 값 설정
  };

  const handleBackClick = () => {
    toggleDrawerProp();
  };

  // 이미지 리사이즈 및 상태 저장
  const handleFileChange = async (e) => {
    e.preventDefault();
    let file = e.target.files[0];

    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(
      now.getHours()
    ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;

    // 파일명 앞에 일시분초를 추가
    const newFileName = `${timestamp}_${salesNo}_${file.name}`;

    setFileName(newFileName); // 선택된 파일의 이름을 상태에 저장

    if (file && file.type.startsWith("image/")) {
      // 이미지 리사이징
      const resizedImage = await resizeImage(file, 1024, 768, 0.7);

      // FileReader를 사용하여 리사이즈된 이미지를 Base64 문자열로 변환
      let reader = new FileReader();
      reader.onloadend = () => {
        //console.log("reader.result", reader.result);
        // 리사이즈된 이미지의 Base64 문자열을 상태에 저장
        setImagePreviewUrl(reader.result); // useState를 사용하여 상태 관리 필요
        setSelectedImage(reader.result); // 리사이즈된 이미지를 서버로 전송하기 위해 상태에 저장
      };
      reader.readAsDataURL(resizedImage);
    }
    // else {
    //     setImagePreviewUrl(""); // 파일이 이미지가 아니거나 선택이 취소된 경우
    // }
  };

  const toggleImageModal = () => {
    setIsImageModalOpen(!isImageModalOpen);
  };

  const fetchInfo = async () => {
    if (orderNo === null || salesNo === null || orderKey === null || customerNo === null) {
      navigate(`/error`);
      return;
    }

    try {
      const result = await CampaignService.TravelerInfo(orderNo, salesNo, orderKey, customerNo);

      if (result) {
        setFormValues(result);
        // setTitle(`${result.한글이름}(${result.성인}) 정보`);
        setTitle(`${result?.한글이름 || ""} 정보`);
        setSelectedGender(result.성별);

        setUsePassport(result.여권발급일필수여부 || false);
        //setUsePassport(false);

        const imageUrl = result.여권사본경로 || "";

        if (imageUrl) {
          const byteArray = await UploadFileService.DownloadEncryptImageAsByteArray(imageUrl, customerNo);

          // TODO: 여기서 바이트 배열을 API 서버에 전송하거나 필요한 처리를 수행합니다.
          const imageExtension = imageUrl.split(".").pop().toLowerCase();
          const mimeType = `image/${imageExtension}`;
          const imageUrlConvert = `data:${mimeType};base64,${byteArray}`;

          setImagePreviewUrl(imageUrlConvert || "");
        } else {
          setImagePreviewUrl("");
        }
      } else {
        navigate(`/error`);
      }
    } catch (error) {
      navigate(`/error`);
    } finally {
    }
  };

  const setFormValues = (data) => {
    Object.keys(data).forEach((key) => {
      setValue(key, data[key] || "");
    });
  };

  useEffect(() => {
    const initFetch = async () => {
      await fetchInfo();
    };
    initFetch();
  }, [orderNo, salesNo, orderKey, customerNo]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const newValue = value.replace(/\D/g, ""); // 숫자가 아닌 모든 문자 제거
    setValue(name, newValue);
  };

  // 입력값을 대문자로 변환하는 핸들러
  const handleUpperCaseInput = (event) => {
    const { name, value } = event.target;
    // 입력값을 대문자로 변환
    const upperCaseValue = value.toUpperCase();
    // 변환된 값을 setValue를 사용하여 업데이트
    setValue(name, upperCaseValue);
  };

  const imageUpload = async () => {
    if (selectedImage) {
      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;

      const filePath = "passportcopy/customer/";

      try {
        const uploadResult = await UploadFileService.uploadEncryptS3({
          File: selectedImage.split(",")[1], // Base64 데이터 추출
          FileName: fileName,
          FilePath: filePath,
          key: customerNo,
        });

        const url = uploadResult.url;

        return { error: false, url: url };
      } catch (error) {
        console.error("업로드 실패:", error);

        return { error: true, message: error.message || "Upload failed" };
        // 에러 처리 로직
      }
    }
  };

  const onSubmit = async (data, event) => {
    event.preventDefault();

    if (data.성별 === "") {
      alert("성별을 선택해주세요.");
      return;
    }

    try {
      let imageUrl = "";

      if (selectedImage) {
        const uploadResult = await imageUpload();

        if (uploadResult.error) {
          alert(uploadResult.message);
          return;
        }

        imageUrl = uploadResult.url;
      }

      const result = await CampaignService.CustomerInfoSave(orderNo, salesNo, orderKey, customerNo, data, imageUrl, usePassport);

      let useImage = false;
      if (imagePreviewUrl || imageUrl) {
        useImage = true;
      }

      toggleDrawerProp(null, data, useImage);
    } catch (error) {
      navigate(`/error`);
    } finally {
    }
  };

  return (
    <form className="bg-white p-4 mx-auto max-w-[500px]" onSubmit={handleSubmit(onSubmit)}>
      <div className="fixed top-0 left-1/2 transform -translate-x-1/2 max-w-[500px] w-full bg-white p-4  rounded-b-0 shadow z-10 ">
        <div className="flex items-center ">
          <button onClick={handleBackClick} className="mr-2">
            <BackIcon className="text-slate-900 h-6 w-6" />
          </button>
          <div className="text-lg font-bold">{title}</div>
        </div>
      </div>
      <div className="mt-[55px]">
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-500" htmlFor="patient-name">
            <span className="text-red-500">*</span>한글이름
          </label>
          <input
            {...register("한글이름", {
              required: "한글이름은 필수 입력입니다.",
            })}
            className="mt-1 block w-full border border-gray-300   py-3 px-3 text-gray-700 leading-tight focus:outline-none  focus:ring-blue-500 focus:border-blue-500  "
            placeholder=""
            // defaultValue={travelerInfo?.한글이름 || ""}
            aria-invalid={isSubmitted ? (errors.한글이름 ? "true" : "false") : undefined}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault(); // 엔터 키 입력 시 기본 동작 방지
                // 필요한 경우 추가 동작 구현
              }
            }}
          />
          {errors.한글이름 && <span className="text-red-500 text-xs">{errors.한글이름.message}</span>}
        </div>

        {/* 성별 선택 버튼 */}
        <div className="mt-4">
          <span className="block text-sm font-medium text-gray-500">
            <span className="text-red-500">*</span>성별
          </span>
          <div className="mt-2 flex">
            <button
              type="button"
              onClick={() => handleSelectGender("남성")}
              className={`mr-2 py-3 px-5 rounded-lg ${(selectedGender || "") === "남성" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-800"}`}
            >
              남성
            </button>
            <button
              type="button"
              onClick={() => handleSelectGender("여성")}
              className={`py-2 px-5 rounded-lg ${(selectedGender || "") === "여성" ? "bg-pink-500 text-white" : "bg-gray-200 text-gray-800"}`}
            >
              여성
            </button>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-500" htmlFor="guardian-name">
            <span className="text-red-500">*</span>영문성
          </label>
          <input
            type="text"
            name="영문성"
            className="mt-1 block w-full border border-gray-300   py-3 px-3 text-gray-700 leading-tight focus:outline-none  focus:ring-blue-500 focus:border-blue-500 "
            placeholder=""
            {...register("영문성", {
              required: "영문성은 필수 입력입니다.",
            })}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault(); // 엔터 키 입력 시 기본 동작 방지
                // 필요한 경우 추가 동작 구현
              }
            }}
          />
          {errors.영문성 && <span className="text-red-500 text-xs">{errors.영문성.message}</span>}
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-500" htmlFor="patient-id">
            <span className="text-red-500">*</span>영문이름
          </label>
          <input
            type="text"
            name="영문이름"
            className="mt-1 block w-full border border-gray-300   py-3 px-3 text-gray-700 leading-tight focus:outline-none  focus:ring-blue-500 focus:border-blue-500 "
            placeholder=""
            {...register("영문이름", {
              required: "영문이름은 필수 입력입니다.",
            })}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault(); // 엔터 키 입력 시 기본 동작 방지
                // 필요한 경우 추가 동작 구현
              }
            }}
          />
          {errors.영문이름 && <span className="text-red-500 text-xs">{errors.영문이름.message}</span>}
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-500" htmlFor="patient-id">
            <span className="text-red-500">*</span>생년월일
          </label>
          <input
            type="number"
            name="생년월일"
            className="mt-1 block w-full border border-gray-300   py-3 px-3 text-gray-700 leading-tight focus:outline-none  focus:ring-blue-500 focus:border-blue-500 "
            placeholder="19820110"
            // {...register("numberField", { required: true })}
            {...register("생년월일", {
              required: "생년월일은 필수 입력입니다.",
              validate: {
                isValidDate: (value) => isValidDate(value) || "유효한 날짜 형식이 아닙니다. YYYYMMDD 형식으로 입력해주세요.",
              },
              //   pattern: {
              //     value: /^\d{8}$/,
              //     message: "생년월일은 YYYYMMDD 형식의 8자리 숫자여야 합니다.",
              //   },
              //   maxLength: {
              //     value: 8,
              //     message: "최대 8자리까지만 입력 가능합니다." // 최대 입력 길이 제한
              //   }
            })}
            onChange={handleInputChange}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault(); // 엔터 키 입력 시 기본 동작 방지
                // 필요한 경우 추가 동작 구현
              }
            }}
          />
          {errors.생년월일 && <span className="text-red-500 text-xs">{errors.생년월일.message}</span>}
        </div>

        {usePassport ? (
          <>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-500" htmlFor="patient-id">
                여권번호
              </label>
              <input
                type="text"
                name="여권번호"
                className="mt-1 block w-full border border-gray-300   py-3 px-3 text-gray-700 leading-tight focus:outline-none  focus:ring-blue-500 focus:border-blue-500 "
                placeholder=""
                {...register("여권번호", {
                  required: "여권번호는 필수 입력입니다.",
                })}
                onChange={handleUpperCaseInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault(); // 엔터 키 입력 시 기본 동작 방지
                    // 필요한 경우 추가 동작 구현
                  }
                }}
              />
              {errors.여권번호 && <span className="text-red-500 text-xs">{errors.여권번호.message}</span>}
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-500" htmlFor="patient-id">
                여권만료일
              </label>
              <input
                type="number"
                name="여권만료일"
                className="mt-1 block w-full border border-gray-300   py-3 px-3 text-gray-700 leading-tight focus:outline-none  focus:ring-blue-500 focus:border-blue-500 "
                placeholder="20300101"
                {...register("여권만료일", {
                  required: "여권만료일은 필수 입력입니다.",
                  validate: {
                    isValidDate: (value) => isValidDate(value) || "유효한 날짜 형식이 아닙니다. YYYYMMDD 형식으로 입력해주세요.",
                  },
                  //   pattern: {
                  //     value: /^\d{8}$/,
                  //     message:
                  //       "여권만료일은 YYYYMMDD 형식의 8자리 숫자여야 합니다.",
                  //   },
                })}
                onChange={handleInputChange}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault(); // 엔터 키 입력 시 기본 동작 방지
                    // 필요한 경우 추가 동작 구현
                  }
                }}
              />
              {errors.여권만료일 && <span className="text-red-500 text-xs">{errors.여권만료일.message}</span>}
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-500" htmlFor="patient-id">
                여권발급일
              </label>
              <input
                type="number"
                name="여권발급일"
                className="mt-1 block w-full border border-gray-300   py-3 px-3 text-gray-700 leading-tight focus:outline-none  focus:ring-blue-500 focus:border-blue-500 "
                placeholder="20300101"
                {...register("여권발급일", {
                  required: "여권발급일은 필수 입력입니다.",
                  validate: {
                    isValidDate: (value) => isValidDate(value) || "유효한 날짜 형식이 아닙니다. YYYYMMDD 형식으로 입력해주세요.",
                  },
                  //   pattern: {
                  //     value: /^\d{8}$/,
                  //     message:
                  //       "여권발급일은 YYYYMMDD 형식의 8자리 숫자여야 합니다.",
                  //   },
                })}
                onChange={handleInputChange}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault(); // 엔터 키 입력 시 기본 동작 방지
                    // 필요한 경우 추가 동작 구현
                  }
                }}
              />
              {errors.여권발급일 && <span className="text-red-500 text-xs">{errors.여권발급일.message}</span>}
            </div>

            {/* 파일 첨부 섹션 */}
            <div className="mt-4">
              <div className="">
                <div>
                  <label htmlFor="passport-copy" className="block text-sm font-medium text-gray-500">
                    여권 사본
                  </label>
                  <input
                    type="file"
                    id="passport-copy"
                    name="passport-copy"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="mt-2 block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-gray-100 file:text-slate-600
                            hover:file:bg-blue-100"
                  />
                </div>
              </div>

              {/* 이미지 미리보기 및 클릭 시 크게 보기 모달 */}
              {imagePreviewUrl && (
                <div className="mt-2 cursor-pointe flex justify-center" onClick={toggleImageModal}>
                  <img src={imagePreviewUrl} alt="Passport Preview" className="max-w-xs max-h-20" />
                </div>
              )}

              {/* 이미지 크게 보기 모달 */}
              {isImageModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onClick={toggleImageModal}>
                  <img src={imagePreviewUrl} alt="Passport Preview" className="max-w-3xl max-h-full" />
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>

      {/* {errorMessage && <AlertMessage type="error" message={errorMessage} />} */}

      {/* 안내 메시지 */}
      <div className="mt-6 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-sm text-blue-700 leading-relaxed">
          <span className="font-semibold">💡 안내사항</span>
          <br />
          여권 발급 및 재발급 고객께서는 여권상 영문명, 생년월일, 성별만 입력해 주시면 됩니다.
        </p>
      </div>

      {/* 저장 버튼 */}
      <div className="mt-6">
        <button
          type="submit"
          className={`w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            isFormValid() && !isSubmitting ? "text-white bg-slate-700 hover:bg-blue-700" : "text-gray-500 bg-gray-200 cursor-not-allowed"
          }`}
          disabled={!isFormValid() || isSubmitting}
        >
          저장
        </button>
      </div>
    </form>
  );
}
